import * as React from "react";
import { Dialog as D } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export const DialogContent = React.forwardRef<
  React.ComponentRef<typeof D.Content>,
  React.ComponentPropsWithoutRef<typeof D.Content> & { hideClose?: boolean; position?: "center" | "top" }
>(function DialogContent({ className, children, hideClose, position = "center", ...props }, ref) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 bg-[#1c1b1a]/25 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out dark:bg-black/45" />
      <D.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 z-50 w-[calc(100vw-32px)] max-w-[480px] -translate-x-1/2 rounded-xl border border-border bg-popover text-popover-foreground shadow-pop outline-none data-[state=open]:animate-sheet-in data-[state=closed]:animate-fade-out",
          position === "center" ? "top-1/2 -translate-y-1/2" : "top-[14vh]",
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <D.Close
            className="absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-hover hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </D.Close>
        )}
      </D.Content>
    </D.Portal>
  );
});

export function DialogTitle({ className, ...props }: React.ComponentPropsWithoutRef<typeof D.Title>) {
  return <D.Title className={cn("font-display text-[17px] font-semibold tracking-[-0.01em]", className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.ComponentPropsWithoutRef<typeof D.Description>) {
  return <D.Description className={cn("text-[13.5px] leading-relaxed text-muted-foreground", className)} {...props} />;
}

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("space-y-3 p-5", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center justify-end gap-2 border-t border-border px-5 py-3", className)} {...props} />;
}

/** Right-side sheet (used by concepts with panel-style detail). */
export const SheetContent = React.forwardRef<
  React.ComponentRef<typeof D.Content>,
  React.ComponentPropsWithoutRef<typeof D.Content> & { side?: "right" | "bottom" }
>(function SheetContent({ className, children, side = "right", ...props }, ref) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 bg-[#1c1b1a]/20 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out dark:bg-black/40" />
      <D.Content
        ref={ref}
        className={cn(
          "fixed z-50 bg-popover text-popover-foreground shadow-pop outline-none data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          side === "right" ? "inset-y-2 right-2 w-[min(480px,calc(100vw-16px))] rounded-xl border border-border" : "inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl border-t border-border",
          className,
        )}
        {...props}
      >
        {children}
      </D.Content>
    </D.Portal>
  );
});
